import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Check } from "lucide-react";
import Reveal from "@/components/Reveal";

const questions = [
  {
    id: 1,
    question: "What is your primary focus right now?",
    options: [
      { label: "Executive leadership and business mastery", value: "coaching_1on1" },
      { label: "A high-impact mindset and resilience reset", value: "reset_workshop" },
      { label: "Personal life and career alignment", value: "balanced_leader" },
      { label: "Corporate team offsite and culture elevation", value: "corporate_programme" },
      { label: "Health, cold water and community connection", value: "community_dips" },
    ],
  },
  {
    id: 2,
    question: "Where are you experiencing the greatest friction?",
    options: [
      { label: "Strategic isolation and heavy decision fatigue at the top", value: "coaching_1on1" },
      { label: "Stress overload, lack of focus, or creeping burnout", value: "reset_workshop" },
      { label: "A gap between work achievement and personal fulfilment", value: "balanced_leader" },
      { label: "Team silos and lost alignment under pressure", value: "corporate_programme" },
      { label: "Physical plateau or a routine that needs shaking up", value: "community_dips" },
    ],
  },
  {
    id: 3,
    question: "Which environment creates your best breakthroughs?",
    options: [
      { label: "Private coaching walks across the Lake District fells", value: "coaching_1on1" },
      { label: "An intensive day of breathwork, cold water and strategy", value: "reset_workshop" },
      { label: "A structured multi-stage framework with accountability", value: "balanced_leader" },
      { label: "A tailored keynote or interactive team workshop", value: "corporate_programme" },
      { label: "An informal Sunday morning dip with good people", value: "community_dips" },
    ],
  },
  {
    id: 4,
    question: "What level of commitment fits your timeline?",
    options: [
      { label: "A dedicated high-touch coaching relationship", value: "coaching_1on1" },
      { label: "One high-intensity reset day", value: "reset_workshop" },
      { label: "A guided pathway over several weeks", value: "balanced_leader" },
      { label: "A bespoke corporate event package", value: "corporate_programme" },
      { label: "A free drop-in Sunday dip, zero commitment", value: "community_dips" },
    ],
  },
];

const results = {
  coaching_1on1: {
    title: "1-to-1 Coaching Walks",
    description: "Tailored strategy sessions with a Master Coach while walking the Lake District fells. Space to think, techniques to change your life, and a heightened sense of self.",
    cta: "Explore 1:1 Coaching",
    link: "/experiences",
    testid: "journey-result-coaching-cta",
  },
  reset_workshop: {
    title: "RX4 RESET Workshop",
    description: "An immersive day combining breathwork, cold water exposure and practical mindset tools. Arrive feeling stuck, leave feeling unstoppable.",
    cta: "View RESET Workshop",
    link: "/events",
    testid: "journey-result-reset-cta",
  },
  balanced_leader: {
    title: "The Balanced Leader",
    description: "Master the 6 Principles: Awareness, Action, Authenticity, Alignment, Accountability and Agility, through our signature 4-stage pathway.",
    cta: "Discover Balanced Leader",
    link: "/balanced-leader",
    testid: "journey-result-balanced-cta",
  },
  corporate_programme: {
    title: "Corporate Programmes",
    description: "Keynotes, workshops and outdoor experiences built to transform team resilience, wellbeing and culture for organisations of any size.",
    cta: "Explore Corporate Programmes",
    link: "/experiences",
    testid: "journey-result-corporate-cta",
  },
  community_dips: {
    title: "Free Sunday Community Dips",
    description: "Join Dave and a welcoming community every Sunday at St Bees Beach. Cold water, calming exercises, and tea and biscuits afterwards.",
    cta: "Join the Sunday Dip",
    link: "/events",
    testid: "journey-result-community-cta",
  },
};

const JourneyFinder = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);

  const select = (value) => {
    const next = [...answers, value];
    setAnswers(next);
    setTimeout(() => {
      if (next.length === questions.length) setDone(true);
      else setStep(step + 1);
    }, 350);
  };

  const restart = () => {
    setAnswers([]);
    setStep(0);
    setDone(false);
  };

  const tally = answers.reduce((acc, v) => ({ ...acc, [v]: (acc[v] || 0) + 1 }), {});
  const winner = Object.keys(results).reduce((a, b) => ((tally[a] || 0) >= (tally[b] || 0) ? a : b));
  const result = results[winner];
  const current = questions[step];

  return (
    <section className="bg-ink-soft border-y border-white/5 text-bone" data-testid="journey-finder-section">
      <div className="section-shell">
        <Reveal className="mb-12 md:mb-16">
          <p className="overline-label mb-4">Interactive Journey Finder</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone max-w-2xl">
            Not sure where to begin? Answer 4 questions.
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl">Discover your tailored entry point into the RX4 framework. It takes under a minute.</p>
        </Reveal>

        <div className="max-w-3xl">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-sm text-brand" data-testid="journey-step-indicator">[ {current.id} / {questions.length} ]</span>
                  <div className="flex gap-2">
                    {questions.map((q, i) => (
                      <span key={q.id} className={`h-1 w-8 rounded-full transition-colors duration-300 ${i <= step ? "bg-brand" : "bg-white/15"}`} />
                    ))}
                  </div>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-8" data-testid={`journey-question-${current.id}`}>
                  {current.question}
                </h3>
                <div className="space-y-3">
                  {current.options.map((opt, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => select(opt.value)}
                      data-testid={`journey-option-${current.id}-${i}`}
                      className="group w-full flex items-center justify-between text-left border border-white/15 rounded-xl px-6 py-4 text-slate-300 transition-colors duration-300 hover:border-brand hover:bg-brand/10"
                    >
                      <span className="text-sm sm:text-base">{opt.label}</span>
                      <ArrowRight size={16} className="text-slate-500 transition-all duration-300 group-hover:text-brand group-hover:translate-x-1" />
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="border border-brand/40 rounded-2xl bg-brand/5 p-8 md:p-12"
                data-testid="journey-result-card"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-6 h-6 rounded-full bg-brand flex items-center justify-center">
                    <Check size={14} strokeWidth={3} className="text-ink" />
                  </span>
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Recommended for you</span>
                </div>
                <h3 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-bone">{result.title}</h3>
                <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">{result.description}</p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to={result.link}
                    data-testid={result.testid}
                    className="group inline-flex items-center gap-2 rounded-full bg-brand text-ink px-7 py-3.5 text-sm font-medium tracking-wide transition-colors duration-300 hover:bg-brand-hover"
                  >
                    {result.cta}
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <button
                    type="button"
                    onClick={restart}
                    data-testid="journey-restart-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-medium tracking-wide text-slate-300 transition-colors duration-300 hover:border-brand hover:text-brand"
                  >
                    <RotateCcw size={14} /> Start over
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default JourneyFinder;
